<template>
	<div class="pickUp-method-form">
		<div class="limitation">
			<div class="limitation-icon">
				<img src="@/assets/notice.png" alt="notice" />
			</div>
			<p class="limitation-text">{{ limitationText }}</p>
		</div>
		<div class="form-row">
			<div v-if="isAvailableNameSwitch" class="input-wrapper">
				<div v-if="isAvailableNameSwitch && isUserNameValid" class="error-hint">
					姓名不能為空
				</div>
				<Input
					title="姓名"
					v-model="userName"
					:class="{ 'error-input': isUserNameValid }"
				/>
			</div>
			<div v-if="isAvailablePhoneSwitch" class="input-wrapper">
				<div v-if="isAvailablePhoneSwitch && isPhoneValid" class="error-hint">
					電話不能為空
				</div>
				<div
					v-if="isAvailablePhoneSwitch && isPhoneFormatValid"
					class="error-hint"
				>
					電話格式不正確
				</div>
				<Input
					title="電話"
					v-model="phone"
					:class="{ 'error-input': isPhoneValid || isPhoneFormatValid }"
				/>
			</div>
		</div>
		<div class="form-row">
			<div class="input-wrapper" style="flex: 1">
				<div v-if="isAddressValid" class="error-hint">地址不能為空</div>
				<Input
					title="地址"
					v-model="address"
					:class="{ 'error-input': isAddressValid }"
				/>
			</div>
		</div>
		<div class="form-row">
			<div class="input-wrapper" style="flex: 1">
				<div v-if="isPickUpDateValid" class="error-hint">取餐時間不能為空</div>
				<ByVuePicker
					:data="displayPickupDateOptions"
					v-model="pickUpDate"
					placeholder="請選擇日期"
					:class="{ 'error-select': isPickUpDateValid }"
				/>
			</div>
			<div class="input-wrapper" style="flex: 1">
				<ByVuePicker
					ref="pickUpTimePkr"
					:data="displayPickupTimeOptions"
					v-model="pickUpTime"
					placeholder="請選擇時間"
					:class="{ 'error-select': isPickUpDateValid }"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import Input from '@/components/Input.vue'
import { getBookableTimeSlots, isTimeSlotSelectable } from '@/utils/booking'

export default {
	name: 'DeliveryForm',
	components: { Input },
	props: {
		maxDistance: {
			type: Number,
			default: 0.5,
		},
		minAmount: {
			type: Number,
			default: 300,
		},
		errorValidate: {
			type: Object,
		},
		pickUpDraft: {
			type: Object,
		},
	},
	data() {
		return {
			address: null,
			userName: null,
			phone: null,
			pickUpDate: null,
			pickUpTime: null,
		}
	},
	computed: {
		limitationText() {
			let text = ''
			if (this.maxDistance && this.minAmount) {
				text = `距離 ${this.maxDistance} km 以內、金額 $${this.minAmount} 以上`
			} else if (this.maxDistance && !this.minAmount) {
				text = `距離 ${this.maxDistance} km 以內`
			} else if (!this.maxDistance && this.minAmount) {
				text = `金額 $${this.minAmount} 以上`
			} else {
				text = '無'
			}
			return `外送限制: ${text}`
		},
		isUserNameValid() {
			return this.errorValidate?.userName
		},
		isPhoneValid() {
			return this.errorValidate?.phone
		},
		isPhoneFormatValid() {
			return this.errorValidate?.phoneFormat
		},
		isAddressValid() {
			return this.errorValidate?.address
		},
		isPickUpDateValid() {
			return this.errorValidate?.pickUpDate
		},
		isPickUpTimeValid() {
			return this.errorValidate?.pickUpTime
		},
		isAvailablePhoneSwitch() {
			return this.$store.state.store?.openInfo?.mobilePickupMethod?.delivery
				?.phoneSwitch
		},
		isAvailableNameSwitch() {
			return this.$store.state.store?.openInfo?.mobilePickupMethod?.delivery
				?.nameSwitch
		},
		displayPickupDateOptions() {
			const list = this.$store.state.store.openInfo.bookingTimeList
			// 無可預約日時後端回的是 []，只擋 null 會讓 picker 拿到 [[]] 而開出一片空白
			if (!list?.length) return [[{ label: '無可選項目', disabled: true }]]

			const weekday = ['日', '一', '二', '三', '四', '五', '六']

			const today = new Date()
			today.setHours(0, 0, 0, 0)

			const options = list.map(item => {
				const d = new Date(item.date)
				d.setHours(0, 0, 0, 0)

				const diff = Math.round((d - today) / 86400000)
				const prefix =
					diff === 0 ? '今天' : diff === 1 ? '明天' : diff === 2 ? '後天' : ''

				const label = `${prefix ? prefix + ' ' : ''}${
					d.getMonth() + 1
				}/${d.getDate()}(${weekday[d.getDay()]})`

				return {
					label,
					value: item.date,
					timeList: item.timeList,
				}
			})

			return [options] // 必須外包一層
		},
		displayPickupTimeOptions() {
			// 沒有選日期 → 回傳空欄位
			if (!this.pickUpDate) return [[{ label: '無可選項目', disabled: true }]]
			const min = this.$store.state.store.openInfo?.minimumBookInterval || 0
			const bookingList = this.$store.state.store.openInfo.bookingTimeList
			return getBookableTimeSlots(bookingList, this.pickUpDate, min)
		},
	},
	methods: {
		// NOTE: 手動去賦值改變本地狀態
		handlePickupDateChange(value) {
			this.pickUpDate = value
		},
		handlePickupTimeChange(value) {
			this.pickUpTime = value
		},
	},
	watch: {
		userName: {
			handler(newVal) {
				this.$emit('update:input', { userName: newVal })
			},
		},
		phone: {
			handler(newVal) {
				this.$emit('update:input', { phone: newVal })
			},
		},
		address: {
			handler(newVal) {
				this.$emit('update:input', { address: newVal })
			},
		},
		pickUpTime: {
			handler(newVal) {
				this.$emit('update:input', { pickUpTime: newVal })
			},
		},
		pickUpDate: {
			handler(newVal) {
				if (!newVal) {
					this.pickUpTime = null
					return
				}

				// 取用 picker 顯示中的同一份清單，避免各自算門檻而在跨分鐘邊界時不一致
				const timeList = this.displayPickupTimeOptions

				// 沿用仍可選的舊值：還原表單時是先設 pickUpDate 再設 pickUpTime，
				// 本 watcher 排到 nextTick 才跑，無條件覆寫會蓋掉還原的時間
				if (!isTimeSlotSelectable(timeList, this.pickUpTime)) {
					const firstSlot = timeList[0]?.find(
						item => item.value && !item.disabled,
					)
					this.pickUpTime = firstSlot?.value ?? null
				}
				this.$emit('update:input', { pickUpDate: newVal })
			},
		},
		displayPickupTimeOptions: {
			handler(newOptions) {
				if (!this.pickUpTime) return

				// 店家中途調整預約設定後，已選時間可能已不在清單中，畫面顯示空白但
				// 驗證照樣放行（後端只擋過去時間，不驗時段是否仍開放）。這裡只清空
				// 不自動遞補，讓使用者重選。
				if (!isTimeSlotSelectable(newOptions, this.pickUpTime)) {
					this.pickUpTime = null
					this.$alert('您選的取餐時間已無法預約，請重新選擇', 'warning')
				}
			},
		},
		pickUpDraft: {
			// 監聽父層傳入的值，渲染需保留的欄位
			immediate: true,
			deep: true,
			handler(val) {
				if (!val) return
				this.address = val.address ?? ''
				this.userName = val.userName ?? ''
				this.phone = val.phone ?? ''
			},
		},
	},
	mounted() {
		// 每次結帳後，新的取餐方式都需記憶使用者資訊
		if (
			this.$store.state.user.pickUpMethod &&
			this.$store.state.user.pickUpMethod === 'DELIVERY'
		) {
			this.address = this.$store.state.user.guestUserPreferences.address
			this.userName = this.$store.state.user.userName
			this.phone = this.$store.state.user.userPhone
			this.pickUpDate = this.$store.state.user.pickUpDate
			this.pickUpTime = this.$store.state.user.pickUpTime
		}

		if (!this.$store.state.user.pickUpMethod) {
			this.userName = this.$store.state.user.userName
			this.phone = this.$store.state.user.userPhone
			this.address = this.$store.state.user.guestUserPreferences.address
		}
	},
}
</script>

<style lang="sass" scoped>
.limitation
  margin-bottom: 10px
  display: flex
  align-items: center
  gap: 4px
.limitation-icon
  width: 32px
  height: 32px
  img
    width: 100%
    height: 100%
.form-row
  display: flex
  flex-wrap: wrap
  .input-wrapper
    width: 48%
    @media screen and (max-width: 420px)
      width: 100%
    &:only-child
      width: 100%
.get-time-title
  font-size: 18px
  font-weight: bold
  padding: 10px 0 15px

.get-time-now
  margin-bottom: 15px

.get-time-time
  flex-wrap: wrap

.get-time-time-content
  width: 100%
  padding-top: 10px

.get-time-time-content-date-select
  margin-bottom: 10px
.error-select
  border: 2px solid red
  border-radius: 50px
</style>
